<template>
  <Teleport to="body">
    <transition name="modal">
      <div v-if="open" class="modal-mask" @wheel.stop @touchmove.stop>
        <!-- Overlay -->
        <div class="fixed inset-0" @click="emit('close')"></div>
        <!-- Panel -->
        <div class="modal-wrapper" @wheel.stop @touchmove.stop>
          <div class="modal-container" :class="sizeClass" @click.stop>
            <div
              class="bg-white px-2 sm:px-4 py-3 rounded-xl border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] flex flex-col max-h-[90vh]"
            >
              <!-- Header with just close button -->
              <div class="flex items-center justify-end flex-shrink-0">
                <button
                  @click="emit('close')"
                  class="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-color cursor-pointer"
                  aria-label="Close"
                >
                  <svg
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Body - Scrollable -->
              <div class="px-4 sm:px-6 pb-2 overflow-y-auto flex-1">
                <slot />
              </div>
              <!-- Footer -->
              <div v-if="slots.footer" :class="footerClasses">
                <slot name="footer" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, useSlots, watch } from "vue";
interface Props {
  open?: boolean;
  title?: string;
  size?: "sm" | "md" | "lg";
  footerClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  title: "",
  size: "lg",
  footerClass: "",
});

const emit = defineEmits<{
  close: [];
}>();

const slots = useSlots();

// Lock body scroll when modal is open
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
  },
  { immediate: true }
);

const sizeClass = computed(() => {
  switch (props.size) {
    case "sm":
      return "w-full max-w-md";
    case "md":
      return "w-full max-w-2xl";
    case "lg":
    default:
      return "w-full max-w-3xl";
  }
});

const footerClasses = computed(() => {
  return props.footerClass || "px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200";
});
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  will-change: opacity;
  overflow: hidden;
  overscroll-behavior: contain;
}

.modal-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.modal-container {
  margin: 0 auto;
  position: relative;
  will-change: transform, opacity;
}

.modal-enter-active {
  transition: opacity 250ms ease-out;
}

.modal-leave-active {
  transition: opacity 200ms ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container {
  transition: transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.modal-leave-active .modal-container {
  transition: transform 200ms ease-in;
}

.modal-enter-from .modal-container {
  transform: scale(0.95) translateY(-10px);
}

.modal-leave-to .modal-container {
  transform: scale(0.95);
}
</style>
